// src/pages/weight-dispute/WeightDisputeDetail.jsx
//
// Merchant-facing Weight Dispute DETAIL (route: /weight-disputes/:disputeId).
//
// This is where the merchant reaches the "consent moment": an OPEN dispute shows exactly what
// they'll be charged, why (full breakup), and gives them two clear choices — Accept the charge,
// or Raise a dispute with evidence — before an auto-accept deadline decides for them.
//
// Everything is revision-scoped. The amount the merchant sees is tied to a revisionNumber; when
// they act we send that number as expectedRevisionNumber. If Wayuvega revised the charge while
// they were looking, the server answers 409 (REVISED_SINCE_VIEWED) and we transparently refetch
// and ask them to review the new amount rather than silently charging the old one.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  Copy,
  Check,
  AlertTriangle,
  Info,
  Loader2,
  ShieldCheck,
  MessageSquare,
  Clock,
  Scale,
  UploadCloud,
  Eye,
  Download,
  Pencil,
  Trash2,
  X,
  History,
  RefreshCw,
  ExternalLink,
  Link2,
} from "lucide-react";
import useAuth from "../../hooks/useAuth";
import {
  fetchDisputeDetail,
  acceptDispute,
  raiseDispute,
  requestEvidenceUpload,
  uploadEvidenceToS3,
  confirmEvidenceUpload,
  addUrlEvidence,
  softDeleteEvidence,
  editEvidenceDescription,
  fetchEvidenceDownloadUrl,
  validateEvidenceFile,
  EVIDENCE_ACCEPT,
  REVISED_SINCE_VIEWED,
  errorCodeOf,
  errorMessageOf,
} from "./weightDisputeApi";
import {
  StatusPill,
  ChargeStatusPill,
  MoneyAmount,
  formatInr,
  formatGrams,
  formatSignedGrams,
  formatDateTime,
  formatBytes,
  actorLabel,
  humanizeEnum,
  isRefund,
} from "./wdUi.jsx";

// Statuses in which the merchant may still add/edit/remove their own evidence.
const EVIDENCE_EDITABLE_STATUSES = new Set(["OPEN", "MERCHANT_DISPUTED"]);

// Evidence may be a FILE (S3-backed) or a URL (external link, e.g. a Drive video). `evidenceKind`
// is absent on legacy rows → treat as FILE.
const isUrlEvidence = (ev) => String(ev?.evidenceKind).toUpperCase() === "URL";

// Best-effort hostname for display ("drive.google.com"). Falls back to the raw string.
const hostnameOf = (url) => {
  if (!url) return "";
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

export default function WeightDisputeDetail() {
  const { disputeId } = useParams();
  const navigate = useNavigate();
  const { getAuthToken } = useAuth();

  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // A soft, dismissable banner used mainly for the 409 "amount changed" notice.
  const [notice, setNotice] = useState(null);

  const [acceptOpen, setAcceptOpen] = useState(false);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadDetail = useCallback(
    async ({ silent } = {}) => {
      const token = getAuthToken?.();
      if (!silent) setLoading(true);
      setError(null);
      try {
        const res = await fetchDisputeDetail(token, disputeId);
        setDispute(res || null);
        return res;
      } catch (err) {
        console.error("fetchDisputeDetail error", err);
        setError(errorMessageOf(err));
        return null;
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [disputeId, getAuthToken]
  );

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // Shared 409 handler for both accept and raise: refetch and surface the "review again" banner.
  const handleRevisedConflict = useCallback(async () => {
    setNotice({
      tone: "amber",
      text: "The charge was revised while you were viewing. Please review the updated amount before you continue.",
    });
    await loadDetail({ silent: true });
  }, [loadDetail]);

  const copyAwb = async () => {
    try {
      await navigator.clipboard.writeText(dispute?.awbNumber || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — ignore silently */
    }
  };

  // --- derived flags ---
  const status = dispute?.status;
  const isOpen = status === "OPEN";
  const isDisputed = status === "MERCHANT_DISPUTED";
  const canEditEvidence = EVIDENCE_EDITABLE_STATUSES.has(status);
  // Legacy (pre-revision-model) docs default to revision 1 — 0 would 409 accept/raise on them.
  const renderedRevision = dispute?.revisionNumber ?? 1;

  const ownActiveEvidences = useMemo(
    () =>
      (dispute?.evidences || []).filter(
        (e) => String(e.uploadedByType).toUpperCase() === "MERCHANT" && !e.markedDeletedAt
      ),
    [dispute]
  );

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error && !dispute) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <BackButton onClick={() => navigate("/weight-disputes")} />
        <div className="mt-4 flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-medium">Couldn&apos;t load this dispute.</p>
            <p className="mt-0.5">{error}</p>
            <button
              onClick={() => loadDetail()}
              className="mt-2 inline-flex items-center gap-1 text-rose-700 underline"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dispute) return null;

  const net =
    dispute.chargeBreakup?.netExtraCharge ??
    dispute.extraChargeAmount ??
    0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-gray-50 px-3 sm:px-5 pt-3 pb-12">
      <div className="mx-auto max-w-5xl">
        <BackButton onClick={() => navigate("/weight-disputes")} />

        {/* Header card */}
        <div className="mt-3 overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-white via-white to-blue-50/50 shadow-sm">
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div className="flex min-w-0 items-start gap-4">
              <div className="hidden h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 shadow-md shadow-blue-500/20 sm:flex">
                <Scale className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  Weight Discrepancy · AWB
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-2xl font-bold tracking-tight text-gray-900">
                    {dispute.awbNumber || "-"}
                  </span>
                  <button
                    onClick={copyAwb}
                    className="rounded-md p-1 text-gray-400 transition hover:bg-white hover:text-gray-700"
                    title="Copy AWB"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500">
                  <span className="truncate" title={dispute.orderReferenceNumber || undefined}>
                    Order ref: {dispute.orderReferenceNumber || "-"}
                  </span>
                  {dispute.orderReferenceNumber ? (
                    <Link
                      to={`/shipment-details/${dispute.orderReferenceNumber}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex flex-shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-blue-600 transition hover:bg-blue-50"
                      title="View order details"
                    >
                      View Order
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  ) : null}
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  <StatusPill status={dispute.status} />
                  <ChargeStatusPill chargeStatus={dispute.chargeStatus} />
                  <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-xs font-semibold tabular-nums text-gray-600">
                    Revision {renderedRevision}
                  </span>
                </div>
              </div>
            </div>

            {/* Big amount — tinted by what it means: pending charge (amber), refund (emerald), settled (neutral) */}
            <div
              className={`rounded-xl border px-5 py-4 text-right sm:min-w-[220px] ${
                isRefund(net)
                  ? "border-emerald-200 bg-emerald-50/70"
                  : isOpen
                    ? "border-amber-200 bg-amber-50/70"
                    : "border-gray-200 bg-white/70"
              }`}
            >
              <div
                className={`text-[11px] font-semibold uppercase tracking-wider ${
                  isRefund(net) ? "text-emerald-600" : isOpen ? "text-amber-600" : "text-gray-400"
                }`}
              >
                {isRefund(net) ? "Refund due" : "Extra charge"}
              </div>
              <div className="mt-1">
                <MoneyAmount amount={net} className="text-3xl font-bold tracking-tight" />
              </div>
              {dispute.amountChargedSoFar > 0 ? (
                <div className="mt-1 text-xs text-gray-500">
                  Charged so far: {formatInr(dispute.amountChargedSoFar)}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* 409 / info notice */}
        {notice ? (
          <div className="mt-3 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <Info className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <span className="flex-1">{notice.text}</span>
            <button onClick={() => setNotice(null)} className="text-amber-700 hover:text-amber-900">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : null}

        {/* State banners */}
        {isOpen ? (
          <OpenConsentBanner
            dispute={dispute}
            onAccept={() => setAcceptOpen(true)}
            onRaise={() => setRaiseOpen(true)}
          />
        ) : null}

        {isDisputed ? (
          <StateBanner
            tone="blue"
            icon={<MessageSquare className="h-5 w-5" />}
            title="Your dispute is under review by Wayuvega"
            body={
              dispute.merchantDisputeDescription ? (
                <div className="mt-1">
                  <span className="text-xs font-medium text-blue-700">What you told us:</span>
                  <p className="mt-0.5 whitespace-pre-wrap text-sm text-blue-900">
                    {dispute.merchantDisputeDescription}
                  </p>
                </div>
              ) : (
                "We&apos;ll update you here once it&apos;s reviewed. You can still add evidence below."
              )
            }
          />
        ) : null}

        {status === "MERCHANT_ACCEPTED" ? (
          <StateBanner
            tone="slate"
            icon={<Check className="h-5 w-5" />}
            title="You accepted this charge"
            body="The extra charge applies and will be settled from your wallet."
          />
        ) : null}

        {status === "DISPUTE_ACCEPTED_BY_WAYUVEGA" ? (
          <StateBanner
            tone="emerald"
            icon={<ShieldCheck className="h-5 w-5" />}
            title="Wayuvega upheld your dispute"
            body={
              isRefund(net)
                ? "A refund is due back to you as shown above."
                : "No extra charge applies for this shipment."
            }
          />
        ) : null}

        {status === "DISPUTE_REJECTED_BY_WAYUVEGA" ? (
          <StateBanner
            tone="rose"
            icon={<AlertTriangle className="h-5 w-5" />}
            title="Wayuvega did not uphold your dispute"
            body="After review, the extra charge proceeds as shown above."
          />
        ) : null}

        {/* Body cards */}
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ChargeBreakupCard breakup={dispute.chargeBreakup} />
          <WeightsCard dispute={dispute} />
        </div>

        <div className="mt-4">
          <RevisionsCard revisions={dispute.revisions} />
        </div>

        <div className="mt-4">
          <MessagesCard notes={dispute.merchantVisibleNotes} />
        </div>

        <div className="mt-4">
          <EvidenceSection
            dispute={dispute}
            disputeId={disputeId}
            canEdit={canEditEvidence}
            onChanged={() => loadDetail({ silent: true })}
            getAuthToken={getAuthToken}
          />
        </div>

        <div className="mt-4">
          <StatusHistoryCard history={dispute.statusHistory} />
        </div>
      </div>

      {/* Accept modal */}
      <AnimatePresence>
        {acceptOpen ? (
          <AcceptModal
            dispute={dispute}
            net={net}
            onClose={() => setAcceptOpen(false)}
            onConfirm={async () => {
              const token = getAuthToken?.();
              await acceptDispute(token, disputeId, renderedRevision);
            }}
            onRevisedConflict={handleRevisedConflict}
            onDone={async () => {
              setAcceptOpen(false);
              setNotice(null);
              await loadDetail({ silent: true });
            }}
          />
        ) : null}
      </AnimatePresence>

      {/* Raise dispute modal */}
      <AnimatePresence>
        {raiseOpen ? (
          <RaiseDisputeModal
            dispute={dispute}
            disputeId={disputeId}
            renderedRevision={renderedRevision}
            ownActiveEvidences={ownActiveEvidences}
            getAuthToken={getAuthToken}
            onClose={() => setRaiseOpen(false)}
            onRevisedConflict={handleRevisedConflict}
            onEvidenceAdded={() => loadDetail({ silent: true })}
            onDone={async () => {
              setRaiseOpen(false);
              setNotice(null);
              await loadDetail({ silent: true });
            }}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}

// ------------------------------------------------------------------ small building blocks

function BackButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 transition hover:text-gray-900"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to Weight Disputes
    </button>
  );
}

const BANNER_TONES = {
  blue: "border-blue-200 bg-blue-50 text-blue-900",
  slate: "border-slate-200 bg-slate-50 text-slate-800",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  rose: "border-rose-200 bg-rose-50 text-rose-900",
  amber: "border-amber-200 bg-amber-50 text-amber-900",
};

function StateBanner({ tone, icon, title, body }) {
  return (
    <div className={`mt-3 flex items-start gap-3 rounded-xl border p-4 ${BANNER_TONES[tone]}`}>
      <div className="mt-0.5 flex-shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className="font-semibold">{title}</p>
        {typeof body === "string" ? <p className="mt-0.5 text-sm">{body}</p> : body}
      </div>
    </div>
  );
}

// The prominent OPEN "consent moment" banner with the two primary actions.
function OpenConsentBanner({ dispute, onAccept, onRaise }) {
  return (
    <div className="mt-3 rounded-xl border-2 border-amber-300 bg-amber-50 p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-6 w-6 flex-shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-amber-900">Action needed</p>
          <p className="mt-0.5 text-sm text-amber-800">
            {isRefund(dispute.chargeBreakup?.netExtraCharge ?? dispute.extraChargeAmount)
              ? "Review this weight variance. A refund may be due to you."
              : "Review this weight-variance charge on your shipment."}{" "}
            {dispute.autoAcceptOnDateTime ? (
              <span className="font-medium">
                If you don&apos;t respond, it auto-accepts on{" "}
                {formatDateTime(dispute.autoAcceptOnDateTime)}.
              </span>
            ) : null}
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <button
              onClick={onAccept}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-700"
            >
              <Check className="h-4 w-4" />
              Accept charge
            </button>
            <button
              onClick={onRaise}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-amber-400 bg-white px-4 py-2 text-sm font-semibold text-amber-800 shadow-sm transition hover:bg-amber-100"
            >
              <MessageSquare className="h-4 w-4" />
              Raise a dispute
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ title, icon, children, right }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          {icon}
          {title}
        </h3>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

// Row helper for the breakup table.
function BreakRow({ label, value, strong, muted, hint }) {
  return (
    <div
      className={`flex items-center justify-between gap-3 py-1.5 ${
        strong ? "border-t border-gray-200 pt-2" : ""
      }`}
    >
      <span className={`text-sm ${muted ? "text-gray-400" : strong ? "font-semibold text-gray-900" : "text-gray-600"}`}>
        {label}
        {hint ? <span className="ml-1 text-xs text-gray-400">{hint}</span> : null}
      </span>
      <span className={`text-sm tabular-nums ${strong ? "font-bold text-gray-900" : "text-gray-800"}`}>
        {value}
      </span>
    </div>
  );
}

// The "why this amount" card — a transparent, line-by-line derivation of the net charge.
function ChargeBreakupCard({ breakup }) {
  if (!breakup) {
    return (
      <Card title="Charge breakup" icon={<Info className="h-4 w-4 text-blue-600" />}>
        <p className="text-sm text-gray-500">Charge breakup isn&apos;t available for this dispute.</p>
      </Card>
    );
  }
  const rtoApplicable = !!breakup.rtoApplicable;
  return (
    <Card title="Charge breakup" icon={<Info className="h-4 w-4 text-blue-600" />}>
      <div className="divide-y-0">
        <p className="mb-2 text-xs text-gray-500">
          This is how the extra amount is calculated from the weight difference.
        </p>

        <BreakRow
          label="Forward charge (declared weight)"
          value={formatInr(breakup.forwardChargeOnChargedWeight)}
        />
        <BreakRow
          label="Forward charge (carrier weight)"
          value={formatInr(breakup.forwardChargeOnCarrierWeight)}
        />
        <BreakRow
          label="Forward difference"
          value={formatInr(breakup.forwardDelta)}
        />

        {rtoApplicable ? (
          <>
            <BreakRow
              label="RTO charge (declared weight)"
              hint={
                breakup.rtoChargePercent != null ? `@ ${breakup.rtoChargePercent}%` : undefined
              }
              value={formatInr(breakup.rtoChargeOnChargedWeight)}
            />
            <BreakRow
              label="RTO charge (carrier weight)"
              value={formatInr(breakup.rtoChargeOnCarrierWeight)}
            />
            <BreakRow label="RTO difference" value={formatInr(breakup.rtoDelta)} />
          </>
        ) : null}

        <BreakRow
          label="Tax on difference"
          hint={breakup.taxPercentage != null ? `@ ${breakup.taxPercentage}%` : undefined}
          value={formatInr(breakup.taxOnDelta)}
        />
        <BreakRow label="Gross extra charge" value={formatInr(breakup.grossExtraCharge)} />
        {breakup.amountAlreadyCharged ? (
          <BreakRow
            label="Already charged"
            value={`- ${formatInr(breakup.amountAlreadyCharged)}`}
            muted
          />
        ) : null}

        <BreakRow
          label="Net extra charge"
          value={<MoneyAmount amount={breakup.netExtraCharge} className="font-bold" />}
          strong
        />
      </div>

      <p className="mt-3 text-[11px] text-gray-400">
        {breakup.zoneId ? <>Zone {breakup.zoneId}</> : null}
        {breakup.zoneId && breakup.computedAt ? " · " : null}
        {breakup.computedAt ? <>Computed {formatDateTime(breakup.computedAt)}</> : null}
      </p>
    </Card>
  );
}

// Declared (snapshot) vs carrier weights, with the signed variance.
function WeightsCard({ dispute }) {
  const snap = dispute.declaredWeightSnapshot || {};
  return (
    <Card title="Declared vs carrier weight" icon={<Scale className="h-4 w-4 text-blue-600" />}>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <Field label="Dead weight" value={formatGrams(snap.deadWeightGrams)} />
        <Field label="Volumetric weight" value={formatGrams(snap.volumetricWeightGrams)} />
        <Field
          label="Dimensions (L×B×H)"
          value={
            snap.lengthCm != null
              ? `${snap.lengthCm} × ${snap.breadthCm} × ${snap.heightCm} cm`
              : "-"
          }
        />
        <Field label="Charged weight (declared)" value={formatGrams(snap.chargedWeightGrams)} />
      </div>

      <div className="mt-3 flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
        <div>
          <div className="text-xs text-gray-500">Carrier-measured weight</div>
          <div className="text-lg font-bold text-gray-900">
            {formatGrams(dispute.carrierWeightGrams)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">Variance</div>
          <div
            className={`text-lg font-bold ${
              (dispute.varianceGrams ?? 0) > 0 ? "text-rose-600" : "text-gray-700"
            }`}
          >
            {formatSignedGrams(dispute.varianceGrams)}
          </div>
        </div>
      </div>
    </Card>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-medium text-gray-900">{value}</div>
    </div>
  );
}

// Timeline of revisions (merchant-visible fields only — no admin/source-file info).
function RevisionsCard({ revisions }) {
  const list = Array.isArray(revisions) ? [...revisions].sort((a, b) => (b.revisionNumber ?? 0) - (a.revisionNumber ?? 0)) : [];
  return (
    <Card title="Weight Discrepancy Revision History" icon={<History className="h-4 w-4 text-blue-600" />}>
      {list.length === 0 ? (
        <p className="text-sm text-gray-500">No revisions yet.</p>
      ) : (
        <ol className="space-y-3">
          {list.map((r) => (
            <li key={r.revisionNumber} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-[11px] font-bold text-blue-700">
                  {r.revisionNumber}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <span className="text-sm font-medium text-gray-900">
                    {formatGrams(r.carrierWeightGrams)}
                  </span>
                  <span className="text-xs text-gray-500">
                    Variance {formatSignedGrams(r.varianceGrams)}
                  </span>
                  <MoneyAmount amount={r.extraChargeAmount} className="text-sm font-semibold" />
                </div>
                {r.note ? <p className="mt-0.5 text-xs text-gray-600">{r.note}</p> : null}
                <p className="mt-0.5 text-[11px] text-gray-400">{formatDateTime(r.revisedAt)}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

// "Messages from Wayuvega" — hidden entirely when there are none.
function MessagesCard({ notes }) {
  const list = Array.isArray(notes) ? [...notes].sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt)) : [];
  if (list.length === 0) return null;
  return (
    <Card title="Messages from Wayuvega" icon={<MessageSquare className="h-4 w-4 text-blue-600" />}>
      <ul className="space-y-3">
        {list.map((n) => (
          <li key={n.noteId} className="rounded-lg bg-blue-50/60 p-3">
            <p className="whitespace-pre-wrap text-sm text-gray-800">{n.note}</p>
            <p className="mt-1 text-[11px] text-gray-400">{formatDateTime(n.addedAt)}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// Compact status-transition timeline.
function StatusHistoryCard({ history }) {
  const list = Array.isArray(history) ? history : [];
  return (
    <Card title="Status history" icon={<Clock className="h-4 w-4 text-blue-600" />}>
      {list.length === 0 ? (
        <p className="text-sm text-gray-500">No status changes yet.</p>
      ) : (
        <ol className="space-y-2">
          {list.map((h, idx) => (
            <li key={idx} className="flex items-start gap-3 text-sm">
              <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-gray-300" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-gray-500">{humanizeEnum(h.fromStatus)}</span>
                  <span className="text-gray-400">→</span>
                  <span className="font-medium text-gray-900">{humanizeEnum(h.toStatus)}</span>
                  <span className="text-xs text-gray-400">by {actorLabel(h.actorType)}</span>
                </div>
                {h.note ? <p className="text-xs text-gray-600">{h.note}</p> : null}
                <p className="text-[11px] text-gray-400">{formatDateTime(h.transitionedAt)}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ evidence

function EvidenceSection({ dispute, disputeId, canEdit, onChanged, getAuthToken }) {
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null); // evidence being description-edited
  const [busyId, setBusyId] = useState(null); // evidenceId currently viewing/downloading/removing
  const [rowError, setRowError] = useState(null);

  const evidences = dispute.evidences || [];

  const openUrl = async (evidenceId, inline) => {
    const token = getAuthToken?.();
    setBusyId(evidenceId);
    setRowError(null);
    try {
      const res = await fetchEvidenceDownloadUrl(token, disputeId, evidenceId, inline);
      const url = res?.downloadUrl || res?.url || res?.presignedUrl;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else setRowError("Couldn't get a link for this file. Please try again.");
    } catch (err) {
      setRowError(errorMessageOf(err));
    } finally {
      setBusyId(null);
    }
  };

  // URL evidence carries its link on the object — open it directly, no download-url round-trip.
  const openExternal = (ev) => {
    if (ev?.url) window.open(ev.url, "_blank", "noopener");
  };

  const removeEvidence = async (evidenceId) => {
    if (
      !window.confirm(
        "Remove this evidence? It stays visible to Wayuvega, greyed out. This cannot be undone."
      )
    )
      return;
    const token = getAuthToken?.();
    setBusyId(evidenceId);
    setRowError(null);
    try {
      await softDeleteEvidence(token, disputeId, evidenceId);
      await onChanged();
    } catch (err) {
      setRowError(errorMessageOf(err));
    } finally {
      setBusyId(null);
    }
  };

  const disabledTitle = canEdit
    ? undefined
    : "Evidence can only be changed while a dispute is open or under review.";

  return (
    <Card
      title="Evidence"
      icon={<UploadCloud className="h-4 w-4 text-blue-600" />}
      right={
        <button
          onClick={() => setAddOpen(true)}
          disabled={!canEdit}
          title={disabledTitle}
          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <UploadCloud className="h-3.5 w-3.5" />
          Add evidence
        </button>
      }
    >
      {rowError ? (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{rowError}</span>
        </div>
      ) : null}

      {evidences.length === 0 ? (
        <p className="text-sm text-gray-500">
          No evidence attached yet.
          {canEdit ? " Add photos, videos, or a link to support your case." : ""}
        </p>
      ) : (
        <ul className="space-y-2">
          {evidences.map((ev) => {
            const isMine = String(ev.uploadedByType).toUpperCase() === "MERCHANT";
            const removed = !!ev.markedDeletedAt;
            const canEditThis = canEdit && isMine && !removed;
            return (
              <EvidenceRow
                key={ev.evidenceId}
                ev={ev}
                isMine={isMine}
                removed={removed}
                busy={busyId === ev.evidenceId}
                canEditThis={canEditThis}
                onOpenExternal={() => openExternal(ev)}
                onView={() => openUrl(ev.evidenceId, true)}
                onDownload={() => openUrl(ev.evidenceId, false)}
                onEdit={() => setEditing(ev)}
                onRemove={() => removeEvidence(ev.evidenceId)}
              />
            );
          })}
        </ul>
      )}

      <AnimatePresence>
        {addOpen ? (
          <AddEvidenceModal
            disputeId={disputeId}
            getAuthToken={getAuthToken}
            onClose={() => setAddOpen(false)}
            onDone={async () => {
              setAddOpen(false);
              await onChanged();
            }}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {editing ? (
          <EditDescriptionModal
            disputeId={disputeId}
            evidence={editing}
            getAuthToken={getAuthToken}
            onClose={() => setEditing(null)}
            onDone={async () => {
              setEditing(null);
              await onChanged();
            }}
          />
        ) : null}
      </AnimatePresence>
    </Card>
  );
}

function EvidenceRow({
  ev,
  isMine,
  removed,
  busy,
  canEditThis,
  onOpenExternal,
  onView,
  onDownload,
  onEdit,
  onRemove,
}) {
  const [showHistory, setShowHistory] = useState(false);
  const history = Array.isArray(ev.descriptionHistory) ? ev.descriptionHistory : [];
  const isUrl = isUrlEvidence(ev);

  return (
    <li
      className={`rounded-lg border p-3 ${
        removed ? "border-gray-200 bg-gray-50 opacity-70" : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                isMine
                  ? "bg-blue-100 text-blue-700"
                  : "bg-purple-100 text-purple-700"
              }`}
            >
              {isMine ? "You" : "Wayuvega"}
            </span>
            {isUrl ? (
              <span className="inline-flex min-w-0 items-center gap-1 text-xs text-gray-500">
                <Link2 className="h-3 w-3 flex-shrink-0 text-gray-400" />
                <span className="truncate" title={ev.url}>
                  {hostnameOf(ev.url)}
                </span>
              </span>
            ) : (
              <>
                <span className="text-xs text-gray-500">{ev.contentType || "file"}</span>
                <span className="text-xs text-gray-400">{formatBytes(ev.sizeBytes)}</span>
              </>
            )}
            {removed ? (
              <span className="text-[11px] font-medium text-rose-600">
                Removed on {formatDateTime(ev.markedDeletedAt)}
              </span>
            ) : null}
          </div>

          <p className={`mt-1 text-sm ${removed ? "text-gray-500 line-through" : "text-gray-800"}`}>
            {ev.description || <span className="italic text-gray-400">No description</span>}
          </p>

          {history.length > 0 ? (
            <button
              onClick={() => setShowHistory((s) => !s)}
              className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
            >
              <Pencil className="h-3 w-3" />
              Edited {history.length}× {showHistory ? "▲" : "▼"}
            </button>
          ) : null}

          {showHistory ? (
            <ul className="mt-1 space-y-1 rounded-md bg-gray-50 p-2">
              {history.map((h, i) => (
                <li key={i} className="text-[11px] text-gray-600">
                  <span className="text-gray-800">{h.description || "(empty)"}</span>
                  <span className="ml-1 text-gray-400">— {formatDateTime(h.editedAt)}</span>
                </li>
              ))}
            </ul>
          ) : null}

          <p className="mt-1 text-[11px] text-gray-400">Uploaded {formatDateTime(ev.uploadedAt)}</p>
        </div>

        <div className="flex flex-shrink-0 items-center gap-1">
          {isUrl ? (
            <button
              onClick={onOpenExternal}
              title="Open link"
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open
            </button>
          ) : (
            <>
              <IconBtn title="View" onClick={onView} busy={busy}>
                <Eye className="h-4 w-4" />
              </IconBtn>
              <IconBtn title="Download" onClick={onDownload} busy={busy}>
                <Download className="h-4 w-4" />
              </IconBtn>
            </>
          )}
          {canEditThis ? (
            <>
              <IconBtn title="Edit description" onClick={onEdit}>
                <Pencil className="h-4 w-4" />
              </IconBtn>
              <IconBtn title="Remove" onClick={onRemove} busy={busy} danger>
                <Trash2 className="h-4 w-4" />
              </IconBtn>
            </>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function IconBtn({ children, title, onClick, busy, danger }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      title={title}
      className={`rounded-md p-1.5 transition disabled:opacity-40 ${
        danger ? "text-rose-600 hover:bg-rose-50" : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
    </button>
  );
}

// ------------------------------------------------------------------ modals

// Backdrop + card shell shared by every modal here.
function ModalShell({ children, onClose, maxWidth = "max-w-lg" }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0, y: 8 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0, y: 8 }}
        className={`w-full ${maxWidth} overflow-hidden rounded-2xl bg-white shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

function ModalHeader({ title, onClose }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <button onClick={onClose} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

// Accept-charge confirmation. Restates the amount so consent is explicit.
function AcceptModal({ dispute, net, onClose, onConfirm, onRevisedConflict, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Charges need a deliberate, explicit acknowledgment before the confirm button arms.
  const [acknowledged, setAcknowledged] = useState(false);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onConfirm();
      await onDone();
    } catch (e) {
      if (errorCodeOf(e) === REVISED_SINCE_VIEWED) {
        onClose();
        await onRevisedConflict();
        return;
      }
      setErr(errorMessageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const refund = isRefund(net);

  return (
    <ModalShell onClose={busy ? () => {} : onClose}>
      <ModalHeader title={refund ? "Confirm" : "Accept charge"} onClose={onClose} />
      <div className="px-5 py-4">
        <p className="text-sm text-gray-600">
          {refund
            ? "You're confirming this weight variance. A refund is due back to you:"
            : "You're accepting the weight-variance charge on this shipment:"}
        </p>
        <div className="mt-3 rounded-xl bg-gray-50 p-4 text-center">
          <div className="text-xs uppercase tracking-wide text-gray-400">
            {refund ? "Refund due" : "Amount"}
          </div>
          <div className="mt-1">
            <MoneyAmount amount={net} className="text-3xl font-bold" />
          </div>
          <div className="mt-1 text-xs text-gray-500">
            AWB {dispute.awbNumber} · Revision {dispute.revisionNumber ?? 1}
          </div>
        </div>
        {!refund ? (
          <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              disabled={busy}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-xs text-gray-700">
              I understand <MoneyAmount amount={net} className="font-semibold" /> will be settled
              from my wallet and this can&apos;t be undone.
            </span>
          </label>
        ) : null}

        {err ? (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{err}</span>
          </div>
        ) : null}
      </div>
      <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
        <button
          onClick={onClose}
          disabled={busy}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={busy || (!refund && !acknowledged)}
          title={!refund && !acknowledged ? "Tick the acknowledgment above to continue" : undefined}
          className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          {refund ? "Confirm" : "Accept charge"}
        </button>
      </div>
    </ModalShell>
  );
}

// Raise a dispute: description + at least one confirmed own ACTIVE evidence. Merchant can upload
// a new file inline via the same 3-step flow. We require >=1 selected evidence id before submit.
function RaiseDisputeModal({
  dispute,
  disputeId,
  renderedRevision,
  ownActiveEvidences,
  getAuthToken,
  onClose,
  onRevisedConflict,
  onEvidenceAdded,
  onDone,
}) {
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  // Keep the selection valid as the evidence list changes (e.g. after an inline upload).
  const availableIds = useMemo(
    () => new Set(ownActiveEvidences.map((e) => e.evidenceId)),
    [ownActiveEvidences]
  );
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => availableIds.has(id)));
      return next;
    });
  }, [availableIds]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const canSubmit = description.trim().length > 0 && selected.size > 0 && !busy;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const token = getAuthToken?.();
      await raiseDispute(token, disputeId, {
        description: description.trim(),
        evidenceIds: [...selected],
        expectedRevisionNumber: renderedRevision,
      });
      await onDone();
    } catch (e) {
      if (errorCodeOf(e) === REVISED_SINCE_VIEWED) {
        onClose();
        await onRevisedConflict();
        return;
      }
      setErr(errorMessageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell onClose={busy ? () => {} : onClose} maxWidth="max-w-xl">
      <ModalHeader title="Raise a dispute" onClose={onClose} />
      <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
        <p className="text-sm text-gray-600">
          Tell us why you disagree with this charge and attach at least one piece of evidence
          (e.g. a packaging photo, a packaging video, or a link to one).
        </p>

        <label className="mt-4 block text-sm font-medium text-gray-700">
          Why are you disputing? <span className="text-rose-500">*</span>
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder="Describe the issue — what the actual weight/dimensions were, and any context that helps."
          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
        />

        <div className="mt-4 flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">
            Evidence <span className="text-rose-500">*</span>
          </label>
          <button
            onClick={() => setUploadOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition hover:bg-blue-100"
          >
            <UploadCloud className="h-3.5 w-3.5" />
            Add new
          </button>
        </div>

        {ownActiveEvidences.length === 0 ? (
          <div className="mt-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-500">
            You haven&apos;t added any evidence yet. Click <strong>Add new</strong> to attach a
            photo, video, or link.
          </div>
        ) : (
          <ul className="mt-2 space-y-2">
            {ownActiveEvidences.map((ev) => (
              <li
                key={ev.evidenceId}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition ${
                  selected.has(ev.evidenceId)
                    ? "border-blue-400 bg-blue-50"
                    : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
                onClick={() => toggle(ev.evidenceId)}
              >
                <input
                  type="checkbox"
                  checked={selected.has(ev.evidenceId)}
                  onChange={() => toggle(ev.evidenceId)}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="min-w-0">
                  <p className="text-sm text-gray-800">
                    {ev.description || (
                      <span className="italic text-gray-400">No description</span>
                    )}
                  </p>
                  {isUrlEvidence(ev) ? (
                    <p className="flex min-w-0 items-center gap-1 text-[11px] text-gray-400">
                      <Link2 className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate" title={ev.url}>
                        {hostnameOf(ev.url)}
                      </span>
                    </p>
                  ) : (
                    <p className="text-[11px] text-gray-400">
                      {ev.contentType} · {formatBytes(ev.sizeBytes)}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {err ? (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{err}</span>
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-5 py-3">
        <span className="text-xs text-gray-400">
          {selected.size} evidence selected
        </span>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
            Submit dispute
          </button>
        </div>
      </div>

      {/* Nested inline add (file upload or link) */}
      <AnimatePresence>
        {uploadOpen ? (
          <AddEvidenceModal
            disputeId={disputeId}
            getAuthToken={getAuthToken}
            onClose={() => setUploadOpen(false)}
            onDone={async (newEvidenceId) => {
              setUploadOpen(false);
              // Pre-select the freshly added evidence so it counts toward the requirement.
              if (newEvidenceId) setSelected((prev) => new Set([...prev, newEvidenceId]));
              await onEvidenceAdded();
            }}
          />
        ) : null}
      </AnimatePresence>
    </ModalShell>
  );
}

// The 3-step evidence upload: request presigned → PUT to S3 (no auth) → confirm. Progress states
// are surfaced so the merchant sees what's happening on larger video uploads.
const UPLOAD_STEPS = {
  idle: "",
  requesting: "Preparing upload…",
  uploading: "Uploading file…",
  confirming: "Finishing up…",
};

const URL_DESCRIPTION_MAX = 1000;

// "Add evidence" hub: one modal, two paths — upload a file or attach an external link. Both land
// in the same `evidences` array (uploadedByType MERCHANT) and call onDone(newEvidenceId) so the
// caller (raise flow) can pre-select the freshly added item.
function AddEvidenceModal({ disputeId, getAuthToken, onClose, onDone }) {
  const [tab, setTab] = useState("file"); // "file" | "link"
  const [busy, setBusy] = useState(false); // locks tab switching / close while a submit is in flight

  return (
    <ModalShell onClose={busy ? () => {} : onClose}>
      <ModalHeader title="Add evidence" onClose={busy ? () => {} : onClose} />

      <div className="border-b border-gray-100 px-5 pt-3">
        <div className="flex gap-1" role="tablist" aria-label="Evidence type">
          <TabButton active={tab === "file"} disabled={busy} onClick={() => setTab("file")}>
            <UploadCloud className="h-4 w-4" />
            Upload file
          </TabButton>
          <TabButton active={tab === "link"} disabled={busy} onClick={() => setTab("link")}>
            <Link2 className="h-4 w-4" />
            Add link
          </TabButton>
        </div>
      </div>

      {tab === "file" ? (
        <FileUploadPanel
          disputeId={disputeId}
          getAuthToken={getAuthToken}
          onClose={onClose}
          onDone={onDone}
          onBusyChange={setBusy}
        />
      ) : (
        <LinkEvidencePanel
          disputeId={disputeId}
          getAuthToken={getAuthToken}
          onClose={onClose}
          onDone={onDone}
          onBusyChange={setBusy}
        />
      )}
    </ModalShell>
  );
}

function TabButton({ active, disabled, onClick, children }) {
  return (
    <button
      role="tab"
      aria-selected={active}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-t-lg border-b-2 px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
        active
          ? "border-blue-600 text-blue-700"
          : "border-transparent text-gray-500 hover:text-gray-800"
      }`}
    >
      {children}
    </button>
  );
}

function FileUploadPanel({ disputeId, getAuthToken, onClose, onDone, onBusyChange }) {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState("");
  const [step, setStep] = useState("idle");
  const [err, setErr] = useState(null);
  const inputRef = useRef(null);

  const busy = step !== "idle";
  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  const onPick = (f) => {
    setErr(null);
    if (!f) return;
    const validationError = validateEvidenceFile(f);
    if (validationError) {
      setErr(validationError);
      setFile(null);
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file) return;
    const token = getAuthToken?.();
    setErr(null);
    try {
      setStep("requesting");
      const req = await requestEvidenceUpload(token, disputeId, file);
      const presignedUrl = req?.presignedUploadUrl || req?.uploadUrl;
      const evidenceId = req?.evidenceId;
      const s3ObjectKey = req?.s3ObjectKey || req?.objectKey;
      if (!presignedUrl || !evidenceId) {
        throw new Error("Upload could not be started. Please try again.");
      }

      setStep("uploading");
      await uploadEvidenceToS3(presignedUrl, file, file.type);

      setStep("confirming");
      await confirmEvidenceUpload(token, disputeId, {
        evidenceId,
        s3ObjectKey,
        description: description.trim(),
      });

      await onDone(evidenceId);
    } catch (e) {
      setErr(errorMessageOf(e));
      setStep("idle");
    }
  };

  return (
    <>
      <div className="px-5 py-4">
        <div
          onClick={() => !busy && inputRef.current?.click()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition ${
            busy ? "border-gray-200 bg-gray-50" : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
          }`}
        >
          <UploadCloud className="h-8 w-8 text-gray-400" />
          {file ? (
            <>
              <p className="mt-2 text-sm font-medium text-gray-800">{file.name}</p>
              <p className="text-xs text-gray-500">{formatBytes(file.size)}</p>
            </>
          ) : (
            <>
              <p className="mt-2 text-sm font-medium text-gray-700">Click to choose a file</p>
              <p className="text-xs text-gray-500">JPEG/PNG up to 10MB · MP4/MOV up to 50MB</p>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={EVIDENCE_ACCEPT}
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0])}
          />
        </div>

        <label className="mt-4 block text-sm font-medium text-gray-700">Description (optional)</label>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Weighing scale showing 1.8 kg"
          disabled={busy}
          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
        />

        {busy ? (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-blue-50 p-2.5 text-sm text-blue-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            {UPLOAD_STEPS[step]}
          </div>
        ) : null}

        {err ? (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{err}</span>
          </div>
        ) : null}
      </div>
      <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
        <button
          onClick={onClose}
          disabled={busy}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={!file || busy}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
          Upload
        </button>
      </div>
    </>
  );
}

// Attach an external http(s) link (e.g. a Drive video) as evidence in one call. The API returns
// the updated dispute; we pick out the just-added URL row so it can be pre-selected upstream.
function LinkEvidencePanel({ disputeId, getAuthToken, onClose, onDone, onBusyChange }) {
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  const trimmedUrl = url.trim();
  const isValidUrl = /^https?:\/\//i.test(trimmedUrl);
  const showUrlError = trimmedUrl.length > 0 && !isValidUrl;
  const canSubmit = isValidUrl && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    const token = getAuthToken?.();
    setBusy(true);
    setErr(null);
    try {
      const updated = await addUrlEvidence(token, disputeId, {
        url: trimmedUrl,
        description: description.trim(),
      });
      // The response is the updated dispute — the new row is the merchant-owned, active URL
      // evidence with the latest uploadedAt.
      const newId = newestOwnUrlEvidenceId(updated, trimmedUrl);
      await onDone(newId);
    } catch (e) {
      setErr(errorMessageOf(e));
      setBusy(false);
    }
  };

  return (
    <>
      <div className="px-5 py-4">
        <label className="block text-sm font-medium text-gray-700">
          Link URL <span className="text-rose-500">*</span>
        </label>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://drive.google.com/…"
          disabled={busy}
          className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 disabled:bg-gray-50 ${
            showUrlError
              ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500"
              : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          }`}
        />
        {showUrlError ? (
          <p className="mt-1 text-xs text-rose-600">
            Enter a full link starting with http:// or https://
          </p>
        ) : (
          <p className="mt-1 text-xs text-gray-400">
            Paste a shareable link (e.g. a Drive video). Make sure Wayuvega can open it.
          </p>
        )}

        <label className="mt-4 block text-sm font-medium text-gray-700">Description (optional)</label>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={URL_DESCRIPTION_MAX}
          placeholder="e.g. Unboxing video showing the package"
          disabled={busy}
          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
        />

        {err ? (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{err}</span>
          </div>
        ) : null}
      </div>
      <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
        <button
          onClick={onClose}
          disabled={busy}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={!canSubmit}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
          Add link
        </button>
      </div>
    </>
  );
}

// From the updated dispute, find the id of the URL evidence we just added: prefer an exact url
// match among the merchant's active URL rows, else fall back to the most recent one.
function newestOwnUrlEvidenceId(dispute, url) {
  const mine = (dispute?.evidences || []).filter(
    (e) =>
      isUrlEvidence(e) &&
      String(e.uploadedByType).toUpperCase() === "MERCHANT" &&
      !e.markedDeletedAt
  );
  const exact = mine.filter((e) => e.url === url);
  const pool = exact.length > 0 ? exact : mine;
  if (pool.length === 0) return null;
  const newest = pool.reduce((latest, e) =>
    new Date(e.uploadedAt || 0) >= new Date(latest.uploadedAt || 0) ? e : latest
  );
  return newest.evidenceId ?? null;
}

// Versioned description edit for own ACTIVE evidence.
function EditDescriptionModal({ disputeId, evidence, getAuthToken, onClose, onDone }) {
  const [description, setDescription] = useState(evidence.description || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const token = getAuthToken?.();
      await editEvidenceDescription(token, disputeId, evidence.evidenceId, description.trim());
      await onDone();
    } catch (e) {
      setErr(errorMessageOf(e));
      setBusy(false);
    }
  };

  return (
    <ModalShell onClose={busy ? () => {} : onClose}>
      <ModalHeader title="Edit description" onClose={onClose} />
      <div className="px-5 py-4">
        <label className="block text-sm font-medium text-gray-700">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-gray-400">
          Your previous description is kept in the edit history.
        </p>
        {err ? (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{err}</span>
          </div>
        ) : null}
      </div>
      <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
        <button
          onClick={onClose}
          disabled={busy}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Save
        </button>
      </div>
    </ModalShell>
  );
}
